import { useDisclosure } from '@mantine/hooks'
import { getMapControlsPointValueTypeAndPointCategory } from 'App/Map/CurrentValuesMap/caches/StationValuesCache/StationBadgeComponent/_utils/getMapControlsPointValueTypeAndPointCategory'
import { openStackemChartsModal } from 'App/Map/CurrentValuesMap/components/SelectedMapCard/components/_utils/openStackemChartsModal'
import { isNil } from 'lodash'
import { useEffect, useRef } from 'react'
import { HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD } from 'settings/hubConnectorEnvironment'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { mapSelectedCardStore } from 'stores/mapSelectedCardStore/mapSelectedCardStore'
import { mapStore } from 'stores/mapStore/mapStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getPrimaryValueGroup } from 'stores/selectedValueGroupsStore/getPrimaryValueGroup'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { usePrimaryValueGroup } from 'utils/usePrimaryValueGroup'
import { useSetMapOverlayZIndex } from 'utils/useSetMapOverlayZIndex'
import type { STATION_META, TStationsValueTypesToPropsDictionary } from '../StationValuesCache'
import { BadgeContent } from './BadgeContent'
import { createBadgeStyles } from './createBadgeStyles'

export type TStationBadgeComponentProps = {
  meta: STATION_META
  valueTypesToPropsDictionary: TStationsValueTypesToPropsDictionary
}

export const StationBadgeComponent = ({ meta, valueTypesToPropsDictionary }: TStationBadgeComponentProps) => {
  const [isHovered, { close, open }] = useDisclosure(false)
  const zoom = mapStore.useSelector((s) => s.zoom)
  const primaryValueGroup = usePrimaryValueGroup()

  const { showMapBadgeNames, valueType, pointCategory } = mapControlsStore.useSelector((s) => {
    const { valueType, pointCategory } = getMapControlsPointValueTypeAndPointCategory({
      mapControlsStoreState: s,
      valueGroup: primaryValueGroup,
      isOutOfBlock: meta.isOutOfBlock,
    })

    return {
      showMapBadgeNames: s.showMapBadgeNames,
      valueType,
      pointCategory,
      otherThingsWeWantToTriggerARefreshOn: [s.heatmapColoring, s.heatmapExtremes],
    }
  })

  const selectedFieldAssets = selectedFieldAssetsStore.useSelector((s) => s)
  const ref = useRef<HTMLDivElement>(null)
  const isSelected = selectedFieldAssets[pointCategory] === meta.lngLat

  useSetMapOverlayZIndex({
    baseZIndex: valueTypesToPropsDictionary?.[String(valueType)]?.baseZIndex,
    ref,
    mItem: 'M_InBlockPoint',
    isSelected,
    isHighlighted: false,
    isHovered,
  })

  useEffect(() => {
    close()
  }, [zoom, close])

  if (isNil(valueType)) return null

  const { children, getContainerCSS, getSize, isWeatherStation, shouldAddDataSourceOverlay, valuesForPoint } =
    valueTypesToPropsDictionary?.[valueType] ?? {}

  const diameter = getSize?.() ?? 50

  if (isNil(children)) return null

  const showLabel = isHovered || showMapBadgeNames

  const handleClick = () => {
    const primaryValueGroup = getPrimaryValueGroup()
    const pointEmitterTypes = meta.emitterTypes
    const selectedEmitters = mapControlsStore.getState().selectedEmitters

    if (primaryValueGroup === 'irrigation_activity_pressure') {
      const zoneId = meta.irrigationZoneId

      const emitterType = selectedEmitters.find((e) => {
        return pointEmitterTypes.includes(e)
      })

      if (!emitterType || !zoneId) {
        setSelectedFieldAsset({ lngLat: meta.lngLat })
      } else {
        const irrigationZoneEmitterId = `${zoneId}|||${emitterType}` as const

        setSelectedFieldAsset({
          irrigationZoneEmitter: irrigationZoneEmitterId,
        })
      }
    } else {
      setSelectedFieldAsset({ lngLat: meta.lngLat })
    }

    mapSelectedCardStore.actions.showStation({
      ...meta,
      irrigationActivity: valuesForPoint,
    })

    if (HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD) {
      openStackemChartsModal()
    }
  }

  return (
    <div
      css={createBadgeStyles(diameter, isSelected, !!isWeatherStation, getContainerCSS?.())}
      ref={ref}
      onClick={handleClick}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      <BadgeContent
        name={meta.name}
        showLabel={showLabel}
        diameter={diameter}
        shouldAddDataSourceOverlay={shouldAddDataSourceOverlay}
      >
        {children}
      </BadgeContent>
    </div>
  )
}
